import React from "react";
import { Button } from "../../../../foundation/altzero_base/base/components/ui/button";
import { Badge } from "../../../../foundation/altzero_base/base/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../foundation/altzero_base/base/components/ui/dropdown-menu";
import { ChevronDown, Lock, RefreshCw, Bot } from "lucide-react";
import { SectionMetadata } from "../../utils/sectionUtils";

interface SectionMetadataDropdownProps {
  sectionId: string;
  currentMetadata: SectionMetadata;
  onMetadataChange: (sectionId: string, metadata: SectionMetadata) => void;
  readonly?: boolean;
}

const METADATA_OPTIONS = [
  { 
    value: 'static' as const, 
    label: 'Static Content', 
    icon: Lock, 
    description: 'Fixed content that cannot be modified by users',
    color: 'bg-gray-100 text-gray-800 border-gray-300'
  },
  { 
    value: 'dynamic' as const, 
    label: 'Dynamic Data', 
    icon: RefreshCw, 
    description: 'Content pulled from external data sources',
    color: 'bg-blue-100 text-blue-800 border-blue-300'
  },
  { 
    value: 'ai-assisted' as const, 
    label: 'AI-Assisted', 
    icon: Bot, 
    description: 'AI can help generate and suggest content',
    color: 'bg-purple-100 text-purple-800 border-purple-300'
  }
];

export default function SectionMetadataDropdown({
  sectionId,
  currentMetadata,
  onMetadataChange,
  readonly = false
}: SectionMetadataDropdownProps) {
  const currentOption = METADATA_OPTIONS.find(opt => opt.value === currentMetadata.type) || METADATA_OPTIONS[0];


  const handleTypeChange = (newType: SectionMetadata['type']) => {
    const newMetadata: SectionMetadata = {
      ...currentMetadata,
      type: newType
    };

    // Reset type-specific fields when changing type
    if (newType !== 'ai-assisted') {
      delete newMetadata.aiPrompt;
    }
    if (newType !== 'dynamic') {
      delete newMetadata.dataSource;
      delete newMetadata.refreshInterval;
    }

    onMetadataChange(sectionId, newMetadata);
  };



  if (readonly) {
    return (
      <Badge variant="secondary" className="text-xs px-2 py-0.5">
        <currentOption.icon className="w-3 h-3 mr-1" />
        {currentOption.value}
      </Badge>
    );
  }

  return (
    <div className="flex items-center">
      {/* Metadata Type Selection Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-3 gap-2 text-xs border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
          >
            <currentOption.icon className="w-3 h-3" />
            <span>{currentOption.value}</span>
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 mb-2 px-2">Section Type</div>
            {METADATA_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => handleTypeChange(option.value)}
                className="flex items-start gap-3 p-3 rounded-md cursor-pointer hover:bg-gray-50"
              >
                <div className={`p-1.5 rounded-md ${option.color}`}>
                  <option.icon className="w-3 h-3" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    {option.description}
                  </div>
                </div>
                {currentMetadata.type === option.value && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                )}
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
